import React, { cloneElement, useState } from "react";
import PropTypes from "prop-types";
import Label from "../Label/Label";
import styles from "./TextField.module.scss";
import UserAvatar from "../UserAvatar/UserAvatar";
import ErrorMessage from "../Form/ErrorMessage";
import clsx from "clsx";

const TextField = React.forwardRef(
  (
    {
      className,
      defaultValue,
      disabled,
      errors,
      icon,
      image,
      label,
      colorLabel,
      labelType,
      maxLength,
      onClick,
      onChange,
      onKeyDown,
      name,
      placeholder,
      readOnly,
      required,
      upperHelpText,
      lowerHelpText,
      type,
      inputProps = {},
      useAvatar,
      errorMessages = {},
      dataTestId,
    },
    ref
  ) => {
    const [charLength, setCharLength] = useState((defaultValue || "").length);

    const IS_ERROR = !!errors[name];

    const inputClasses = clsx("input-group", {
      [styles.invalid]: IS_ERROR,
      [styles.inputGroupDisabled]: disabled,
      [styles.inputGroup]: !disabled,
      [styles.primary]: !disabled && !IS_ERROR,
    });

    const textFieldClasses = clsx(styles.textField, className);

    const helperTextClasses = clsx(styles.helpText, "mb-4");

    return (
      <div className={textFieldClasses} data-testid="TextField">
        {label ? (
          <Label
            disabled={disabled}
            color={colorLabel}
            htmlFor={name}
            required={required}
            type={labelType}
          >
            {label}
          </Label>
        ) : null}
        {upperHelpText ? (
          <span className={helperTextClasses} data-testid="UpperHelpText">
            {upperHelpText}
          </span>
        ) : null}
        <div className={inputClasses} data-testid="TextFieldContainer">
          {image || useAvatar ? (
            <div className={`input-group-prepend ${styles.inputGroupPrepend}`}>
              <span className={`input-group-text ${styles.inputGroupText}`}>
                {useAvatar ? (
                  <UserAvatar image={image} name={defaultValue} className={styles.avatar} />
                ) : (
                  <img src={image} className={styles.image} alt="" />
                )}
              </span>
            </div>
          ) : null}
          <input
            data-testid={dataTestId}
            autoComplete={name}
            disabled={disabled}
            id={name}
            name={name}
            className="form-control"
            defaultValue={defaultValue}
            type={type || "text"}
            maxLength={maxLength}
            onClick={onClick}
            onChange={(e) => {
              onChange && onChange(e.target.value);
              setCharLength(e.target.value.length);
            }}
            onKeyDown={(e) =>
              e.key === 13 && onKeyDown ? onKeyDown() : e.key === 13 ? e.preventDefault() : null
            }
            placeholder={placeholder}
            readOnly={readOnly}
            ref={ref}
            {...inputProps}
          />
          {icon ? (
            <div className={clsx("input-group-append", styles.inputGroupAppend)}>
              <span className={clsx("input-group-text", styles.inputGroupText)}>
                {typeof icon === "string" ? (
                  <img src={icon} className={styles.image} alt="" />
                ) : (
                  cloneElement(icon, { className: styles.image })
                )}
              </span>
            </div>
          ) : null}
        </div>
        <div className="d-flex justify-content-between flex-row-reverse">
          {maxLength ? (
            <span className="text-footer" data-testid="MaxLength">
              ({charLength} / {maxLength})
            </span>
          ) : null}
        </div>
        <ErrorMessage errorMessages={errorMessages} errors={errors} label={label} name={name} />
        {lowerHelpText ? (
          <span className={`${styles.helpText} mt-4`} data-testid="LowerHelpText">
            {lowerHelpText}
          </span>
        ) : null}
      </div>
    );
  }
);

TextField.propTypes = {
  defaultValue: PropTypes.any,
  disabled: PropTypes.bool,
  errors: PropTypes.shape({
    type: PropTypes.string,
    message: PropTypes.string,
  }),
  errorMessages: PropTypes.object,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  image: PropTypes.string,
  label: PropTypes.string,
  colorLabel: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  upperHelpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  lowerHelpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  type: PropTypes.string,
  dataTestId: PropTypes.string,
};

TextField.defaultProps = {
  disabled: false,
  errors: {},
  errorMessages: {},
  icon: "",
  image: "",
  label: "",
  colorLabel: "default",
  name: "",
  placeholder: "",
  readOnly: false,
  required: false,
  upperHelpText: "",
  lowerHelpText: "",
  type: "",
  dataTestId: "TextFieldInput",
};

export default TextField;
